import React from "react";
import { debounce, get, isEqual, map, size, trim } from "lodash";
import classnames from "classnames";
import Shape from "./Shape";
import Versions from "./Versions";
import { dispatch, select } from "@wordpress/data";

import { FilePond } from "react-filepond";

import { saveTracks } from "../helpers/save-tracks";

import AddToPlaylist from "./AddToPlaylist";
import { Highlight } from "react-instantsearch-dom";

const debouncedSaveTracks = debounce(saveTracks, 500);

class TrackEditableRow extends React.PureComponent {
  constructor(props) {
    super(props);
    this.waveform = React.createRef();
    this.versionClickHandler = this.versionClickHandler.bind(this);
    this.getTrackKey = this.getTrackKey.bind(this);
    this.togglePlaying = this.togglePlaying.bind(this);
    this.toggleAddingTrackOn = this.toggleAddingTrackOn.bind(this);
    this.toggleAddingTrackOff = this.toggleAddingTrackOff.bind(this);
    this.setupWaveSurfer = this.setupWaveSurfer.bind(this);
    this.handleFilePondInit = this.handleFilePondInit.bind(this);

    this.togglePlayingDebounced = debounce(this.togglePlaying, 500, {
      leading: true,
    });

    this.state = {
      track: props.track,
      version: props.version,
      waveSurfer: null,
      waveSurferSettingUp: false,
      isPlaying: false,
      showPlayer: false,
      isAddingTrack: false,
      files: [],
    };
  }

  handleFilePondInit() {
    // console.log("FilePond instance has initialised", this.pond);
  }

  versionClickHandler(versionNumber) {
    const { playingTrack } = this.props;
    const { setPlayingTrack } = dispatch("hifi");
    const { isPlaying, waveSurfer } = this.state;
    const trackKey = this.getTrackKey(versionNumber);
    const isMyTrackKey = playingTrack === trackKey;

    if (isPlaying) {
      waveSurfer.pause();

      this.setState({
        isPlaying: !isPlaying,
      });
    } else {
      if (!isMyTrackKey) {
        setPlayingTrack(trackKey);
      } else {
        waveSurfer.play();

        this.setState({
          isPlaying: true,
        });
      }
    }
  }

  setupWaveSurfer() {
    // TODO get from state....
    // const { track, version } = this.props;
    const { track, version } = this.state;
    const songURL = get(version, "hifi_song_version_url");

    this.setState({
      waveSurferSettingUp: true,
    });

    const waveSurfer = WaveSurfer.create({
      audioContext: window.HIFI_APP.audioContext, // todo, make this come from store?
      container: this.waveform.current,
      waveColor: "#420b0a",
      progressColor: "#DC2521",
      cursorColor: "transparent",
      height: 46,
    });

    waveSurfer.on("ready", () => {
      if (this.state.isPlaying) {
        this.state.waveSurfer.play();
      } else {
        if (this.state.waveSurfer.isPlaying()) {
          this.state.waveSurfer.pause();
        }

        this.state.waveSurfer.destroy();
        this.setState({
          waveSurfer: null,
        });
      }

      this.setState({
        waveSurferSettingUp: false,
      });
    });

    waveSurfer.load(songURL);

    this.setState({
      waveSurfer,
      // waveSurferSettingUp: false,
    });
  }

  getTrackKey(versionNumberOverride) {
    const { track, versionNumber } = this.props;
    const useVersionNumber = versionNumberOverride || versionNumber;
    return `${track.objectID}-${useVersionNumber}`;
  }

  toggleAddingTrackOn(versionNumberOverride) {
    const { addingTrack } = this.props;
    const { isAddingTrack } = this.state;
    const { setAddingTrack } = dispatch("hifi");

    const trackKey = this.getTrackKey(versionNumberOverride);

    if (!isAddingTrack) {
      if (addingTrack !== trackKey) {
        setAddingTrack(trackKey);
      }

      this.setState({
        isAddingTrack: !isAddingTrack,
      });
    }
  }

  toggleAddingTrackOff() {
    this.setState({
      isAddingTrack: false,
    });
  }

  togglePlaying(versionNumberOverride) {
    const { playingTrack } = this.props;
    const { isPlaying, waveSurfer, waveSurferSettingUp } = this.state;
    const { setPlayingTrack } = dispatch("hifi");

    const trackKey = this.getTrackKey(versionNumberOverride);

    if (!isPlaying) {
      const isMyTrackKey = playingTrack === trackKey;
      if (!isMyTrackKey) {
        // stop old track, before starting this one
        setPlayingTrack(trackKey);
      }

      if (!waveSurfer) {
        // setup then play
        this.setupWaveSurfer();
      } else {
        // play
        waveSurfer.play();
      }
    } else {
      // pause
      waveSurfer.pause();

      // hide waveform after 2 seconds of paused/un-use
      setTimeout(() => {
        const { isPlaying } = this.state;
        if (!isPlaying) {
          this.setState({
            showPlayer: false,
          });
        }
      }, 2000);
    }

    this.setState({
      isPlaying: !isPlaying,
      showPlayer: true,
    });
  }

  componentDidUpdate(prevProps) {
    const { playingTrack, addingTrack } = this.props;
    const { isPlaying, waveSurfer } = this.state;
    const trackKey = this.getTrackKey();

    if (!isEqual(this.props.track, prevProps.track)) {
      this.setState((state, props) => ({
        track: props.track,
      }));
    }

    // playingTrack changed
    if (playingTrack !== prevProps.playingTrack) {
      // pause if not the playing track.

      if (playingTrack !== trackKey) {
        if (waveSurfer) {
          // if (isPlaying) {
          // this.togglePlaying();
          // }

          // pause
          if (waveSurfer.isPlaying()) {
            waveSurfer.pause();
          }
        }

        this.setState({
          isPlaying: false,
          showPlayer: false,
        });
      } else {
        // this is mine
        if (!isPlaying) {
          this.togglePlaying();
        }
      }
    }

    if (addingTrack !== prevProps.addingTrack && addingTrack !== trackKey) {
      this.toggleAddingTrackOff();
    }
  }

  componentWillUnmount() {
    const { isPlaying, waveSurfer } = this.state;
    if (isPlaying) {
      waveSurfer.pause();
      waveSurfer.destroy();
    }
  }

  render() {
    const {
      // track,
      // version,
      versionNumber,
      versionsExpanded,
      // versionHighlightResults,
      toggleVersionsExpanded,
      energyDisplayColumn,
      shapeDisplayColumn,
      isSelected,
    } = this.props;

    const { track, version, isPlaying, showPlayer, isAddingTrack } = this.state;

    const trackKey = this.getTrackKey();

    // const { getSelectedTracks } = select("hifi");
    // const selectedTracks = getSelectedTracks();
    // const isSelected = "undefined" !== typeof selectedTracks[trackKey];

    const versionsCount = size(track.versions);
    const expanded = versionsCount > 1 && versionsExpanded;
    const isVisible = 1 === versionNumber || versionsExpanded;

    const title =
      1 === versionNumber ? track.title : version.hifi_song_version_title;
    const length =
      1 === versionNumber ? track.length : version.hifi_song_version_length;
    // const titleVersionMatch =
    //   1 === versionNumber
    //     ? track._highlightResult.title
    //     : versionHighlightResults;

    return (
      <div
        key={track.id}
        className={classnames({
          item: true,
          visible: isVisible,
          expanded: expanded,
          subtrack: versionNumber > 1,
        })}
      >
        <div className="col col-checkbox">
          <input
            type="checkbox"
            value={track.objectID}
            checked={isSelected}
            onChange={(e) => {
              const { addTrackToSelected, removeTrackFromSelected } =
                dispatch("hifi");

              if (e.target.checked) {
                addTrackToSelected(track, trackKey);
              } else {
                removeTrackFromSelected(trackKey);
              }

              // this.setState({
              //   isSelected: !!e.target.checked,
              // });
            }}
          />
        </div>
        <div
          className={classnames({
            col: true,
            // "first-col": true,
            "col-title": true,
            "has-versions": versionsCount > 1,
          })}
          onClick={(e) => {
            console.log("------------------------------");
            console.log("e.target.tagName");
            console.log(e.target.tagName);
            console.log("title clicked, maybe expanding versions");

            console.log("versionsExpanded");
            console.log(versionsExpanded);

            if (versionsCount > 1) {
              if ("INPUT" === e.target.tagName) {
                console.log("clicked inside the input");
                if (!versionsExpanded) {
                  console.log("not expanded, doing that now");
                  toggleVersionsExpanded();
                }
              } else {
                console.log("clicked outside the input");
                toggleVersionsExpanded();
              }
            }

            console.log("------------------------------");
          }}
          title={title}
        >
          <input
            value={title}
            onChange={(e) => {
              if (1 === versionNumber) {
                this.setState({
                  track: { ...track, title: e.target.value },
                });

                debouncedSaveTracks([
                  {
                    id: track.objectID,
                    property: "title",
                    value: e.target.value,
                    algoliaValue: e.target.value,
                  },
                ]);
              } else {
                this.setState({
                  version: {
                    ...version,
                    hifi_song_version_title: e.target.value,
                  },
                });

                const versionNumberKey = versionNumber - 1;
                const meta_key = `hifi_song_version_${versionNumberKey}_hifi_song_version_title`;

                debouncedSaveTracks([
                  {
                    id: track.objectID,
                    property: meta_key,
                    value: e.target.value,
                    algoliaValue: e.target.value,
                  },
                ]);
              }
            }}
          />
        </div>
        <div className="col col-id">{track.objectID}</div>
        <div className="col col-artist">
          {/* <span>{get(track, "artist")}</span> */}
          <select
            value={get(track, "artist[0]")}
            onChange={(e) => {
              this.setState({
                track: { ...track, artist: e.target.value },
              });

              const artist = window.HIFI_APP.artists.find(
                (artist) => e.target.value === artist.name
              );

              if (artist) {
                saveTracks([
                  {
                    id: track.objectID,
                    property: "artist",
                    value: artist.id,
                    algoliaValue: artist.name,
                  },
                ]);
              }
            }}
          >
            {map(window.HIFI_APP.artists, (artist) => (
              <option key={artist.id} value={artist.name}>
                {artist.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col col-keyword">
          {/* TODO, make this be select2 style */}
          <input
            type="text"
            value={get(track, "keyword", []).join(", ")}
            onChange={(e) => {
              alert(
                "Select this track's checkbox then use the Set Keywords bulk edit tool to edit this field."
              );
            }}
          />
        </div>
        <div className="col col-length">
          {/* {length} */}
          <input
            value={track.length_sort}
            type="number"
            onChange={(e) => {
              alert(
                "Please use wp-admin to edit a Track's length, this field is pretty complex behind the scenes."
              );
              // this.setState({
              //   track: { ...track, length_sort: e.target.value },
              // });

              // TODO, this is more complex, bc there is
              // track level "hifi_song_length" field, then we break
              // that down to length and length_sort for algolia,
              // then there is also a per verion length
            }}
          />
        </div>
        <div className="col col-energy">
          {/* {track.energy} */}
          <select
            value={track.energy}
            onChange={(e) => {
              alert(
                "This value is derived from the bpm, please edit the bpm itself."
              );
            }}
          >
            <option value="Downtempo">Downtempo</option>
            <option value="Midtempo">Midtempo</option>
            <option value="Uptempo">Uptempo</option>
            <option value="High Energy">High Energy</option>
          </select>
        </div>
        <div className="col col-bpm">
          <input
            type="number"
            value={track.bpm}
            onChange={(e) => {
              this.setState({
                track: { ...track, bpm: e.target.value },
              });

              debouncedSaveTracks([
                {
                  id: track.objectID,
                  property: "bpm",
                  value: e.target.value,
                  algoliaValue: e.target.value,
                },
              ]);
            }}
          />
        </div>
        {/* <div className="col col-shape">
          <Shape shapeName={track.shape_name} shapeClass={track.shape_class} />
        </div> */}
        <div className="col col-shape">
          {/* {track.shape_name} */}
          <select
            value={track.shape_name}
            onChange={(e) => {
              const shape_name = e.target.value;
              this.setState({
                track: { ...track, shape_name },
              });

              saveTracks([
                {
                  id: track.objectID,
                  property: "shape_name",
                  value: shape_name,
                  algoliaValue: shape_name,
                },
              ]);
            }}
          >
            <option value="Steady">Steady</option>
            <option value="Builds">Builds</option>
            <option value="Intro">Intro</option>
            <option value="Two Sections">Two Sections</option>
            <option value="A Journey">A Journey</option>
            <option value="Downhill">Downhill</option>
            <option value="Problem/Solution">Problem/Solution</option>
          </select>
        </div>
        <div className="col col-customizable">
          <select
            value={track.customizable ? "Yes" : "No"}
            onChange={(e) => {
              this.setState({
                track: { ...track, customizable: "Yes" === e.target.value },
              });

              saveTracks([
                {
                  id: track.objectID,
                  property: "customizable",
                  value: "Yes" === e.target.value ? "1" : "0",
                  algoliaValue: "Yes" === e.target.value,
                },
              ]);
            }}
          >
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className="col col-status">
          <select
            value={track.status}
            onChange={(e) => {
              this.setState({
                track: { ...track, status: e.target.value },
              });

              saveTracks([
                {
                  id: track.objectID,
                  property: "status",
                  value: e.target.value,
                  algoliaValue: e.target.value,
                },
              ]);
            }}
          >
            <option value="publish">Publish</option>
            <option value="draft">Draft</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="col col-versions">
          <Versions
            versions={track.versions}
            versionNumber={versionNumber}
            versionsExpanded={versionsExpanded}
            isPlaying={isPlaying}
            clickHandler={this.versionClickHandler}
            versionsExpandedHandler={toggleVersionsExpanded}
          />
        </div>
        <div className="col col-waveform">
          {!showPlayer && (
            <div className="filepond">
              <FilePond
                ref={(ref) => (this.pond = ref)}
                files={this.state.files}
                allowMultiple={false}
                allowReorder={false}
                maxFiles={1}
                allowRevert={false}
                server={{
                  url: HIFI_APP.ajax_url,
                  process: {
                    ondata: (formData) => {
                      formData.set(
                        "upload_mp3_nonce",
                        HIFI_APP.upload_mp3_nonce
                      );
                      formData.set("action", "hifi_handle_ajax_media_upload");
                      formData.set("post_id", track.objectID);
                      formData.set("version_number", versionNumber);

                      return formData;
                    },
                    onload: (response) => {
                      const parsedResponse = JSON.parse(response);

                      if (parsedResponse.success) {
                        const newMP3URL = parsedResponse.data.url;

                        this.setState({
                          version: {
                            ...version,
                            hifi_song_version_url: newMP3URL,
                          },
                        });

                        const { waveSurfer } = this.state;

                        if (waveSurfer) {
                          // blow out the old waveSurfer instance
                          waveSurfer.pause();
                          waveSurfer.destroy();
                        }

                        // (re-)setup the new instance
                        this.setupWaveSurfer();

                        setTimeout(() => {
                          // clear files state
                          this.setState({
                            files: [],
                          });
                        }, 2000);

                        // }
                      } else {
                        alert(
                          "Something went wrong, please refresh the page and try again."
                        );
                      }
                    },
                  },
                }}
                name="files"
                oninit={() => this.handleFilePondInit()}
                onupdatefiles={(fileItems) => {
                  // Set currently active file objects to this.state
                  this.setState({
                    files: fileItems.map((fileItem) => fileItem.file),
                  });
                }}
              />
            </div>
          )}
          <div
            className={classnames({
              waveform: true,
              "show-player": showPlayer,
            })}
            ref={this.waveform}
          ></div>
        </div>
        <div className="col col-buttons">
          <div
            className={classnames({
              btn: true,
              "btn-play": true,
              playing: isPlaying,
            })}
            onClick={() => {
              if (!this.state.waveSurferSettingUp) {
                this.togglePlayingDebounced();
              }
            }}
          >
            <span></span>
          </div>
          <a
            className={classnames({
              btn: true,
              "btn-edit": true,
            })}
            target="_blank"
            title="Edit in wp-admin"
            href={`/wp-admin/post.php?post=${track.objectID}&action=edit`}
          >
            <span></span>
          </a>
        </div>
      </div>
    );
  }
}

export default TrackEditableRow;
